import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MobileLayout from '../components/MobileLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Heart, Play, Pause, SkipForward, ArrowLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Profile picture component with active/inactive state - moved outside to avoid re-creation
const ProfilePic = ({ src, name, isActive, size = 'large', isPlaceholder = false }) => {
  const sizeClasses = size === 'large' ? 'w-24 h-24' : 'w-16 h-16';
  const textSizeClass = size === 'large' ? 'text-3xl' : 'text-xl';
  
  // Show placeholder for unconnected partner
  if (isPlaceholder) {
    return (
      <div className={`flex flex-col items-center transition-all duration-500 ${isActive ? 'scale-110' : 'scale-90 opacity-40'}`}>
        <div className={`${sizeClasses} rounded-full border-4 border-dashed ${isActive ? 'border-primary bg-primary/10' : 'border-gray-300 bg-gray-100'} flex items-center justify-center`}>
          <span className={`${textSizeClass} ${isActive ? 'text-primary' : 'text-gray-400'}`}>?</span>
        </div>
        <p className={`text-sm font-medium mt-2 ${isActive ? 'text-primary' : 'text-gray-400'}`}>{name}</p>
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col items-center transition-all duration-500 ${isActive ? 'scale-110' : 'scale-90 opacity-40'}`}>
      <div className={`${sizeClasses} rounded-full overflow-hidden border-4 ${isActive ? 'border-primary shadow-pink animate-pulse' : 'border-gray-200 grayscale'}`}>
        <img 
          src={src || 'https://via.placeholder.com/96'} 
          alt={name}
          className="w-full h-full object-cover"
        />
      </div>
      <p className={`text-sm font-medium mt-2 ${isActive ? 'text-primary' : 'text-gray-400'}`}>{name}</p>
    </div>
  );
};

const ConflictResolutionContent = ({ user }) => {
  // Onboarding state (show only once ever)
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    return localStorage.getItem('conflictOnboardingSeen') === 'true';
  });
  
  // Main flow state
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [firstSpeakerId, setFirstSpeakerId] = useState(null);
  
  // User info
  const [partnerName, setPartnerName] = useState('Partner');
  const [userName, setUserName] = useState('You');
  const [partnerPicture, setPartnerPicture] = useState(null);
  const [userPicture, setUserPicture] = useState(null);
  const [hasPartner, setHasPartner] = useState(null); // null = loading, true/false = loaded
  
  // Option 2 specific state
  const [option2Step, setOption2Step] = useState(0);
  
  const fetchConnectionInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/connection-info`, { withCredentials: true });
      setHasPartner(response.data?.has_partner || false);
      if (response.data?.partner?.name) {
        setPartnerName(response.data.partner.name.split(' ')[0]);
        setPartnerPicture(response.data.partner.picture);
      }
      if (user?.name) {
        setUserName(user.name.split(' ')[0]);
        setUserPicture(user.picture);
      }
    } catch (error) {
      console.error('Error fetching connection info:', error);
      setHasPartner(false);
    }
  };
  
  const checkActiveSession = async () => {
    try {
      const response = await axios.get(`${API}/conflict/active`, { withCredentials: true });
      if (response.data.session) {
        const sessionCreated = new Date(response.data.session.created_at);
        const now = new Date();
        const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
        
        if (sessionCreated > twoHoursAgo) {
          setActiveSession(response.data.session);
          setSelectedOption(response.data.session.option);
          setCurrentStep(response.data.session.current_step);
        } else {
          await abandonSession(response.data.session.session_id);
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
    }
  };
  
  const abandonSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/conflict/${sessionId}`, { withCredentials: true });
      setActiveSession(null);
      setSelectedOption(null);
      setCurrentStep(0);
      setTimeLeft(0);
      setIsRunning(false);
      setIsPaused(false);
    } catch (error) {
      console.error('Error abandoning session:', error);
    }
  };
  
  const handleResetAndGoBack = async () => {
    if (activeSession) {
      await abandonSession(activeSession.session_id);
    }
    setActiveSession(null);
    setSelectedOption(null);
    setCurrentStep(0);
    setTimeLeft(0);
    setIsRunning(false);
    setIsPaused(false);
    setShowResetConfirm(false);
    setOption2Step(0);
    toast.success('Session ended');
  };
  
  useEffect(() => {
    const init = async () => {
      await checkActiveSession();
      await fetchConnectionInfo();
    };
    init();
  // We only want this to run once on mount
  }, []); // eslint-disable-line
  
  useEffect(() => {
    let interval;
    if (isRunning && !isPaused && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            toast.success('Time complete!');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, isPaused, timeLeft]);
  
  const completeOnboarding = () => {
    localStorage.setItem('conflictOnboardingSeen', 'true');
    setHasSeenOnboarding(true);
  };
  
  const startSession = async (option, speakerId = null) => {
    try {
      const response = await axios.post(
        `${API}/conflict/start`,
        { option, first_speaker_id: speakerId },
        { withCredentials: true, params: { option, first_speaker_id: speakerId } }
      );
      setActiveSession(response.data);
      setSelectedOption(option);
      setCurrentStep(0);
      setFirstSpeakerId(speakerId);
      setIsPaused(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start');
    }
  };
  
  const startTimer = (duration) => {
    setTimeLeft(duration);
    setIsRunning(true);
    setIsPaused(false);
  };
  
  const togglePause = () => {
    setIsPaused(!isPaused);
  };
  
  const skipToNext = async () => {
    const newStep = currentStep + 1;
    setCurrentStep(newStep);
    setTimeLeft(0);
    setIsRunning(false);
    setIsPaused(false);
    
    if (activeSession) {
      try {
        await axios.put(
          `${API}/conflict/${activeSession.session_id}/step`,
          { step: newStep },
          { withCredentials: true, params: { step: newStep } }
        );
      } catch (error) {
        console.error('Error updating step:', error);
      }
    }
  };
  
  const completeSession = async () => {
    try {
      if (activeSession) {
        await axios.put(
          `${API}/conflict/${activeSession.session_id}/complete`,
          {},
          { withCredentials: true }
        );
      }
      toast.success('Session completed! 💕');
      setActiveSession(null);
      setSelectedOption(null);
      setCurrentStep(0);
      setIsPaused(false);
      setOption2Step(0);
    } catch (error) {
      toast.error('Failed to complete session');
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // ==================== ONBOARDING (First time only) ====================
  if (!hasSeenOnboarding) {
    return (
      <MobileLayout>
        <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-120px)]">
          <div className="flex-1 flex flex-col items-center justify-center text-center animate-in fade-in duration-500">
            <div className="text-6xl mb-6">🤝</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Disagreements are normal!</h1>
            
            {/* Info box below title */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 mt-6 max-w-xs">
              <p className="text-gray-600 text-sm mb-3">
                Healthy couples have about <span className="font-semibold text-primary">1 Negative interaction</span> every 5 positive ones
              </p>
              <p className="text-gray-500 text-sm">
                Solving issues quickly can bring you <span className="font-semibold">closer than ever</span>
              </p>
            </div>
            
            <Button
              onClick={completeOnboarding}
              className="w-full max-w-xs h-14 rounded-2xl gradient-brand shadow-pink text-white font-semibold mt-8"
            >
              Let&apos;s Begin
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  // ==================== MAIN SELECTION SCREEN ====================
  if (!selectedOption) {
    return (
      <MobileLayout>
        <div className="p-5 space-y-6" data-testid="conflict-selection">
          {/* Header */}
          <div className="text-center pt-4 pb-2">
            <h1 className="text-2xl font-bold text-gray-900">Conflict Resolution</h1>
            <p className="text-gray-500 text-sm mt-1">Choose your preferred outcome</p>
          </div>
          
          {/* Option 1 - Deeper Understanding */}
          <button
            onClick={() => setSelectedOption('setup1')}
            data-testid="option1-btn"
            className="w-full bg-white rounded-2xl border border-gray-100 shadow-soft p-5 text-left tap-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <span className="text-2xl">💭</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Deeper Understanding</h3>
                <p className="text-sm text-gray-500 mt-1">Take time to listen, validate and understand each other</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>
          
          {/* Option 2 - Laugh Together */}
          <button
            onClick={() => setSelectedOption('setup2')}
            data-testid="option2-btn"
            className="w-full bg-white rounded-2xl border border-gray-100 shadow-soft p-5 text-left tap-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <span className="text-2xl">😂</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Laugh Together</h3>
                <p className="text-sm text-gray-500 mt-1">Sit close facing each other, resolve through deep presence</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        </div>
      </MobileLayout>
    );
  }
  
  // ==================== OPTION 1 SETUP: WHO HAS THE ISSUE ====================
  if (selectedOption === 'setup1' && !activeSession) {
    return (
      <MobileLayout>
        <div className="p-5 space-y-6" data-testid="speaker-selection">
          <button 
            onClick={() => setSelectedOption(null)}
            className="flex items-center gap-2 text-gray-500 tap-feedback"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          {/* Both profile pictures */}
          <div className="flex justify-center gap-8 py-6">
            <ProfilePic src={userPicture} name={userName} isActive={true} />
            <ProfilePic src={partnerPicture} name={hasPartner ? partnerName : "Partner"} isActive={true} isPlaceholder={!hasPartner} />
          </div>
          
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900">Who has the issue to discuss?</h2>
            <p className="text-gray-500 text-sm mt-1">Select who will speak first</p>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => startSession('option1', user.user_id)}
              data-testid="you-first-btn"
              className="w-full h-14 rounded-xl gradient-brand shadow-pink text-base font-semibold text-white"
            >
              I have something to discuss
            </Button>
            
            <Button
              onClick={() => startSession('option1', user.partner_id)}
              data-testid="partner-first-btn"
              variant="outline"
              className="w-full h-14 rounded-xl border-2 border-gray-200 text-base font-semibold"
            >
              {partnerName} has something to discuss
            </Button>
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  // ==================== OPTION 1 ACTIVE: STEPS ====================
  if (selectedOption === 'option1' && activeSession) {
    const speakerIsUser = (currentStep % 2 === 0) === (firstSpeakerId === user.user_id);
    const currentSpeaker = speakerIsUser ? userName : partnerName;
    const currentPicture = speakerIsUser ? userPicture : partnerPicture;
    const isUserActive = speakerIsUser;
    
    const steps = [
      { title: 'Express your feelings', instruction: `${currentSpeaker}, it is your turn to express your feelings.\n\nStart with "I feel..."\n\nYour partner must stay silent and listen.`, duration: 60 },
      { title: 'Reflect back', instruction: `${speakerIsUser ? partnerName : userName}, repeat what you heard.\n\nStart with "You feel..."\n\nShow that you understand.`, duration: 60 },
      { title: 'Express your feelings', instruction: `${speakerIsUser ? partnerName : userName}, now it's your turn.\n\nStart with "I feel..."\n\nYour partner must stay silent and listen.`, duration: 60 },
      { title: 'Reflect back', instruction: `${currentSpeaker}, repeat what you heard.\n\nStart with "You feel..."\n\nShow that you understand.`, duration: 60 },
      { title: 'Offer solutions', instruction: `${currentSpeaker}, offer two possible solutions.\n\nThink about what would work for both of you.`, duration: 60 },
      { title: 'Offer solutions', instruction: `${speakerIsUser ? partnerName : userName}, offer two possible solutions.\n\nThink about what would work for both of you.`, duration: 60 },
      { title: 'Seal it with love', instruction: 'Come together and seal your resolution with a kiss or hug. 💕', duration: 0 }
    ];
    
    const step = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;
    const progress = ((currentStep + 1) / steps.length) * 100;
    
    return (
      <MobileLayout>
        <div className="p-5 space-y-4" data-testid="conflict-option1-active">
          {/* Exit button */}
          <button 
            onClick={() => setShowResetConfirm(true)}
            className="flex items-center gap-2 text-gray-500 tap-feedback"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Exit</span>
          </button>
          
          {/* Reset confirmation dialog */}
          {showResetConfirm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
                <h3 className="text-lg font-bold text-gray-900 text-center">Exit Session?</h3>
                <p className="text-gray-500 text-sm text-center">This will end your current session.</p>
                <div className="flex gap-3">
                  <Button onClick={() => setShowResetConfirm(false)} variant="outline" className="flex-1 h-11 rounded-xl">
                    Continue
                  </Button>
                  <Button onClick={handleResetAndGoBack} className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600">
                    Exit
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full gradient-brand rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
          
          {/* Profile pictures - dynamic active state */}
          {!isLastStep && (
            <div className="flex justify-center gap-8 py-4">
              <ProfilePic src={userPicture} name={userName} isActive={isUserActive} size="large" />
              <ProfilePic src={partnerPicture} name={hasPartner ? partnerName : "Partner"} isActive={!isUserActive} size="large" isPlaceholder={!hasPartner} />
            </div>
          )}
          
          {/* Step info */}
          <div className="text-center py-2">
            <h2 className="text-xl font-bold text-gray-900">{step.title}</h2>
            <p className="text-gray-500 text-sm mt-3 whitespace-pre-line">{step.instruction}</p>
          </div>
          
          {/* Timer */}
          {!isLastStep && (
            <div className="flex justify-center py-4">
              <div className="relative w-40 h-40">
                <svg className="transform -rotate-90 w-40 h-40">
                  <circle cx="80" cy="80" r="72" stroke="#F3F4F6" strokeWidth="8" fill="transparent" />
                  <circle
                    cx="80" cy="80" r="72"
                    stroke="url(#gradient)"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={`${2 * Math.PI * 72}`}
                    strokeDashoffset={`${2 * Math.PI * 72 * (1 - timeLeft / step.duration)}`}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FF6B9D" />
                      <stop offset="100%" stopColor="#FFB74D" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{formatTime(timeLeft)}</p>
                    {isPaused && <p className="text-xs text-primary font-medium mt-1">PAUSED</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Kiss screen */}
          {isLastStep && (
            <div className="text-center py-8">
              <div className="text-7xl mb-4">💕</div>
            </div>
          )}
          
          {/* Controls */}
          <div className="space-y-3">
            {!isLastStep && !isRunning && timeLeft === 0 && (
              <Button onClick={() => startTimer(step.duration)} className="w-full h-14 rounded-xl gradient-brand shadow-pink text-base font-semibold">
                <Play className="w-5 h-5 mr-2" fill="white" />
                Start
              </Button>
            )}
            
            {isRunning && timeLeft > 0 && (
              <div className="flex gap-3">
                <Button onClick={togglePause} variant="outline" className="flex-1 h-14 rounded-xl border-2 border-gray-200 text-base font-semibold">
                  {isPaused ? <><Play className="w-5 h-5 mr-2" />Resume</> : <><Pause className="w-5 h-5 mr-2" />Pause</>}
                </Button>
                <Button onClick={skipToNext} variant="outline" className="flex-1 h-14 rounded-xl border-2 border-gray-200 text-base font-semibold">
                  <SkipForward className="w-5 h-5 mr-2" />
                  Skip
                </Button>
              </div>
            )}
            
            {!isRunning && timeLeft === 0 && !isLastStep && currentStep > 0 && (
              <Button onClick={skipToNext} className="w-full h-14 rounded-xl gradient-brand shadow-pink text-base font-semibold">
                Next Step
              </Button>
            )}
            
            {isLastStep && (
              <Button onClick={completeSession} className="w-full h-14 rounded-xl gradient-brand shadow-pink text-base font-semibold">
                Complete
              </Button>
            )}
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  // ==================== OPTION 2 SETUP: LAUGH TOGETHER ====================
  if (selectedOption === 'setup2' && !activeSession) {
    return (
      <MobileLayout>
        <div className="p-5 space-y-6" data-testid="option2-setup">
          <button 
            onClick={() => setSelectedOption(null)}
            className="flex items-center gap-2 text-gray-500 tap-feedback"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          {/* Both profile pictures */}
          <div className="flex justify-center gap-6 py-4">
            <ProfilePic src={userPicture} name={userName} isActive={true} />
            <ProfilePic src={partnerPicture} name={hasPartner ? partnerName : "Partner"} isActive={true} isPlaceholder={!hasPartner} />
          </div>
          
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Laugh Together</h2>
            <p className="text-gray-500">Resolve through deep presence and connection</p>
          </div>
          
          {/* Steps */}
          <div className="space-y-4 bg-gray-50 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Sit facing each other</p>
                <p className="text-sm text-gray-500">Find a comfortable spot and sit close</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Move closer</p>
                <p className="text-sm text-gray-500">Until your noses are touching</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Start discussing</p>
                <p className="text-sm text-gray-500">You have 5 minutes to resolve your issue</p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={() => startSession('option2')}
            className="w-full h-14 rounded-xl gradient-brand shadow-pink text-base font-semibold text-white"
          >
            We&apos;re Ready
          </Button>
        </div>
      </MobileLayout>
    );
  }
  
  // ==================== OPTION 2 ACTIVE ====================
  if (selectedOption === 'option2' && activeSession) {
    return (
      <MobileLayout>
        <div className="p-5 space-y-6" data-testid="conflict-option2-active">
          <button 
            onClick={() => setShowResetConfirm(true)}
            className="flex items-center gap-2 text-gray-500 tap-feedback"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Exit</span>
          </button>
          
          {/* Reset confirmation dialog */}
          {showResetConfirm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
                <h3 className="text-lg font-bold text-gray-900 text-center">Exit Session?</h3>
                <p className="text-gray-500 text-sm text-center">This will end your current session.</p>
                <div className="flex gap-3">
                  <Button onClick={() => setShowResetConfirm(false)} variant="outline" className="flex-1 h-11 rounded-xl">
                    Continue
                  </Button>
                  <Button onClick={handleResetAndGoBack} className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600">
                    Exit
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Profile pictures */}
          <div className="flex justify-center gap-6 py-2">
            <ProfilePic src={userPicture} name={userName} isActive={isRunning} size="large" />
            <ProfilePic src={partnerPicture} name={hasPartner ? partnerName : "Partner"} isActive={isRunning} size="large" isPlaceholder={!hasPartner} />
          </div>
          
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900">Laugh Together</h2>
            <p className="text-gray-500 text-sm mt-1">5 minutes to connect and resolve</p>
          </div>
          
          {/* Timer */}
          <div className="flex justify-center py-4">
            <div className="relative w-44 h-44">
              <svg className="transform -rotate-90 w-44 h-44">
                <circle cx="88" cy="88" r="80" stroke="#F3F4F6" strokeWidth="8" fill="transparent" />
                <circle
                  cx="88" cy="88" r="80"
                  stroke="url(#gradient2)"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={`${2 * Math.PI * 80}`}
                  strokeDashoffset={`${2 * Math.PI * 80 * (1 - timeLeft / 300)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                />
                <defs>
                  <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FF6B9D" />
                    <stop offset="100%" stopColor="#FFB74D" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-4xl font-bold text-gray-900">{formatTime(timeLeft)}</p>
                  {isPaused && <p className="text-xs text-primary font-medium mt-1">PAUSED</p>}
                </div>
              </div>
            </div>
          </div>
          
          {/* Controls */}
          <div className="space-y-3">
            {!isRunning && timeLeft === 0 && (
              <Button onClick={() => startTimer(300)} className="w-full h-14 rounded-xl gradient-brand shadow-pink text-base font-semibold">
                <Play className="w-5 h-5 mr-2" fill="white" />
                Start
              </Button>
            )}
            
            {isRunning && timeLeft > 0 && (
              <Button onClick={togglePause} variant="outline" className="w-full h-14 rounded-xl border-2 border-gray-200 text-base font-semibold">
                {isPaused ? <><Play className="w-5 h-5 mr-2" />Resume</> : <><Pause className="w-5 h-5 mr-2" />Pause</>}
              </Button>
            )}
            
            <Button onClick={completeSession} variant="outline" className="w-full h-14 rounded-xl border-2 border-gray-200 text-base font-semibold">
              End Session
            </Button>
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  return null;
};

const ConflictResolution = () => {
  return (
    <ProtectedRoute>
      <ConflictResolutionContent />
    </ProtectedRoute>
  );
};

export default ConflictResolution;
